import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Modal, Button, Spin, Tooltip } from 'antd';
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  X,
} from 'lucide-react';
import { Scene, SceneTransition } from '../../../../types/scene';
import SceneService from '../../services/sceneService';
import logger from '../../utils/logger';
import { MultiSceneCompositorPlayer, MultiSceneCompositorPlayerRef } from '../../compositor/components/MultiSceneCompositorPlayer';
import { buildRecordingDataFromScene } from '../../compositor/utils/sceneToRecordingData';
import { CompositorRecordingData } from '../../compositor/types/manifest';
import './ProjectPlayerModal.scss';

interface ProjectPlayerModalProps {
  visible: boolean;
  projectId?: string;
  scenes: Scene[];
  onClose: () => void;
}

interface SceneWithVideo extends Scene {
  videoUrl: string;
  startTime: number;
  endTime: number;
  transitionOut?: SceneTransition;
  transitionDuration?: number;
  halfTransition?: number;
}

// Playback state machine for clean transition management
interface PlaybackState {
  sceneIndex: number;
  isTransitioning: boolean;
  transitionType: string;
  transitionProgress: number;
}

interface SceneInput {
  scene: Scene;
  recordingData: CompositorRecordingData;
}

export const ProjectPlayerModal: React.FC<ProjectPlayerModalProps> = ({
  visible,
  projectId,
  scenes,
  onClose,
}) => {
  // ===== REFS (Mutable, synchronous access) =====
  const videoRefsArray = useRef<(HTMLVideoElement | null)[]>([]);
  const compositorRef = useRef<MultiSceneCompositorPlayerRef>(null);
  const currentTimeRef = useRef<number>(0);
  const animationFrameRef = useRef<number | null>(null);
  const lastFrameTimeRef = useRef<number>(0);
  const isResettingRef = useRef<boolean>(false);
  
  // Playback state (updated synchronously in animation loop)
  const playbackStateRef = useRef<PlaybackState>({
    sceneIndex: 0,
    isTransitioning: false,
    transitionType: 'cut',
    transitionProgress: 0,
  });
  
  // Ref for pauseAllVideos to avoid dependency issues in useEffect
  const pauseAllVideosRef = useRef<() => void>(() => {});
  
  // Ref for isPlaying to avoid re-renders in ended listener
  const isPlayingRef = useRef<boolean>(false);
  
  // Refs for scene state to avoid infinite loops in animation useEffect
  const currentSceneIndexRef = useRef<number>(0);
  const isTransitioningRef = useRef<boolean>(false);
  
  // Ref for throttling currentTime state updates (update every 100ms instead of every frame)
  const lastTimeUpdateRef = useRef<number>(0);
  
  // ===== STATE (UI rendering only) =====
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scenesWithVideos, setScenesWithVideos] = useState<SceneWithVideo[]>([]);
  const [currentSceneIndex, setCurrentSceneIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionProgress, setTransitionProgress] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // ===== COMPOSITOR STATE =====
  const [useCompositor, setUseCompositor] = useState(false);
  const [compositorSceneInputs, setCompositorSceneInputs] = useState<SceneInput[]>([]);
  const [compositorReady, setCompositorReady] = useState(false);
  
  // Memoized callbacks for compositor to prevent re-renders
  const handleCompositorTimeUpdate = useCallback((timeMs: number) => {
    setCurrentTime(timeMs / 1000);
  }, []);
  
  const handleCompositorPlay = useCallback(() => {
    setIsPlaying(true);
  }, []);
  
  const handleCompositorPause = useCallback(() => {
    setIsPlaying(false);
  }, []);
  
  const handleCompositorEnded = useCallback(() => {
    setIsPlaying(false);
  }, []);
  
  const handleCompositorReady = useCallback(() => {
    setCompositorReady(true);
  }, []);
  
  // Reset playback to beginning
  const performPlaybackReset = useCallback(() => {
    isResettingRef.current = true;
    
    // Cancel animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    // Reset all videos
    videoRefsArray.current.forEach((video, idx) => {
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
    });
    
    // Reset refs
    currentTimeRef.current = 0;
    lastFrameTimeRef.current = 0;
    playbackStateRef.current = {
      sceneIndex: 0,
      isTransitioning: false,
      transitionType: 'cut',
      transitionProgress: 0,
    };
    
    // Reset states
    setCurrentTime(0);
    setCurrentSceneIndex(0);
    setIsTransitioning(false);
    setTransitionProgress(0);
    setIsPlaying(false);
    
    setTimeout(() => {
      isResettingRef.current = false;
    }, 50);
  }, []);

  // Load all recorded scenes with videos
  useEffect(() => {
    if (!visible || scenes.length === 0) {
      setScenesWithVideos([]);
      setCompositorSceneInputs([]);
      setUseCompositor(false);
      setLoading(false);
      return;
    }

    const loadSceneVideos = async () => {
      setLoading(true);
      setError(null);
      setCompositorReady(false);
      
      try {
        logger.info('[ProjectPlayer] Loading scenes with videos:', { totalScenes: scenes.length });

        // First pass: collect scene data with transitions
        const sceneData: Array<{
          scene: any;
          durationInSeconds: number;
          transitionOut: any;
          transitionDuration: number;
          recordingData: CompositorRecordingData | null;
        }> = [];

        let allSeparateStreams = true;

        for (const scene of scenes) {
          if (scene.status !== 'recorded') {
            logger.info('[ProjectPlayer] Skipping unrecorded scene:', {
              id: scene.id,
              title: scene.title,
              status: scene.status,
            });
            continue;
          }

          const freshScene = await SceneService.getSceneById(scene.id, projectId);
          
          logger.info('[ProjectPlayer] Fresh scene fetched:', {
            id: freshScene?.id,
            title: freshScene?.title,
            hasVideoUrl: !!freshScene?.videoUrl,
            videoUrl: freshScene?.videoUrl,
            duration: freshScene?.duration,
          });
          
          if (!freshScene) continue;
          
          // Check if this is a separate_streams recording
          const recordingData = buildRecordingDataFromScene(freshScene);
          const isSeparateStreams = recordingData !== null;
          
          if (!isSeparateStreams) {
            allSeparateStreams = false;
          }
          
          logger.info('[ProjectPlayer] Scene recording type check:', {
            id: freshScene.id,
            title: freshScene.title,
            isSeparateStreams,
            hasRecordingData: !!recordingData,
          });
          
          if (freshScene.videoUrl || recordingData) {
            const durationInSeconds = (freshScene.duration || recordingData?.duration || 0) / 1000;
            
            let transitionOut: any = { type: 'cut', duration: 0 };
            try {
              const sceneWithMetadata = freshScene as any;
              const metadata = typeof sceneWithMetadata.metadata === 'string' 
                ? JSON.parse(sceneWithMetadata.metadata) 
                : sceneWithMetadata.metadata;
              if (metadata?.transitionOut) {
                transitionOut = metadata.transitionOut;
              }
            } catch (err) {
              console.error('[ProjectPlayer] Failed to parse metadata for scene:', freshScene.title, err);
            }
            
            const transitionDuration = transitionOut.type === 'cut' ? 0 : (transitionOut.duration || 0);
            
            sceneData.push({
              scene: freshScene,
              durationInSeconds,
              transitionOut,
              transitionDuration,
              recordingData,
            });
          } else {
            logger.warn('[ProjectPlayer] Scene missing video URL:', {
              id: scene.id,
              title: scene.title,
            });
          }
        }

        // Determine if we should use compositor mode
        // Use compositor if ALL scenes are separate_streams recordings
        const shouldUseCompositor = allSeparateStreams && sceneData.length > 0 && 
          sceneData.every(s => s.recordingData !== null);
        
        logger.info('[ProjectPlayer] Compositor mode decision:', {
          allSeparateStreams,
          sceneCount: sceneData.length,
          shouldUseCompositor,
        });

        setUseCompositor(shouldUseCompositor);

        if (shouldUseCompositor) {
          // Build compositor scene inputs
          const compositorInputs: SceneInput[] = sceneData
            .filter(s => s.recordingData !== null)
            .map(s => ({
              scene: s.scene,
              recordingData: s.recordingData!,
            }));
          
          setCompositorSceneInputs(compositorInputs);
          
          // Calculate total duration accounting for transitions
          let totalMs = 0;
          sceneData.forEach((s, i) => {
            const durationMs = s.recordingData?.duration || s.scene.duration || 0;
            totalMs += durationMs;
            if (i < sceneData.length - 1) {
              totalMs -= (s.transitionDuration * 1000);
            }
          });
          setTotalDuration(totalMs / 1000);
          
          logger.info('[ProjectPlayer] Compositor mode initialized:', {
            inputCount: compositorInputs.length,
            totalDurationSeconds: totalMs / 1000,
          });
        } else {
          // Legacy mode: build timeline with transition-aware timestamps
          const loadedScenes: SceneWithVideo[] = [];
          let timelineCursor = 0;

          for (let i = 0; i < sceneData.length; i++) {
            const { scene, durationInSeconds, transitionOut, transitionDuration } = sceneData[i];
            
            if (!scene.videoUrl) continue;
            
            const startTime = timelineCursor;
            const endTime = startTime + durationInSeconds;
            
            loadedScenes.push({
              ...scene,
              videoUrl: scene.videoUrl,
              startTime,
              endTime,
              transitionOut,
              transitionDuration,
              videoDuration: durationInSeconds,
            });
            
            const isLastScene = i === sceneData.length - 1;
            if (isLastScene) {
              timelineCursor += durationInSeconds;
            } else {
              timelineCursor += durationInSeconds - transitionDuration;
            }
          }

          setScenesWithVideos(loadedScenes);
          setTotalDuration(timelineCursor);
        }
      } catch (err) {
        logger.error('[ProjectPlayer] Failed to load scenes:', err);
        setError('Failed to load project videos');
      } finally {
        setLoading(false);
      }
    };

    loadSceneVideos();
  }, [visible, scenes, projectId]);

  // Stop all videos helper - ensures all media stops
  const stopAllVideos = useCallback(() => {
    // Cancel any animation frame first
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    // Pause all videos before clearing refs
    videoRefsArray.current.forEach((video) => {
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
    });
  }, []);

  // Pause all videos - used when seeking to prevent audio overlap
  const pauseAllVideos = useCallback(() => {
    videoRefsArray.current.forEach((video) => {
      if (video) {
        video.pause();
      }
    });
  }, []);
  
  // Keep refs updated for use in useEffect without causing re-renders
  pauseAllVideosRef.current = pauseAllVideos;
  isPlayingRef.current = isPlaying;
  currentSceneIndexRef.current = currentSceneIndex;
  isTransitioningRef.current = isTransitioning;

  // Reset when modal closes
  useEffect(() => {
    if (!visible) {
      // Stop all videos FIRST, before resetting state
      stopAllVideos();
      
      setIsPlaying(false);
      setCurrentTime(0);
      currentTimeRef.current = 0;
      setCurrentSceneIndex(0);
      setError(null);
      setIsTransitioning(false);
      setTransitionProgress(0);
      lastFrameTimeRef.current = 0;
      
      // Clear refs AFTER stopping videos
      videoRefsArray.current = [];
    }
  }, [visible, stopAllVideos]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAllVideos();
    };
  }, [stopAllVideos]);

  // Aggressive pre-buffering: Start loading ALL videos immediately when modal opens (LEGACY MODE ONLY)
  // Wait for ALL videos to be ready before enabling playback
  // In compositor mode, Remotion handles media preloading internally
  useEffect(() => {
    if (!visible || scenesWithVideos.length === 0 || useCompositor) {
      return;
    }

    setLoading(true);
    
    // Track which videos are ready
    const videosReady = new Set<number>();
    const totalVideos = scenesWithVideos.length;
    const cleanupFns: (() => void)[] = [];
    let isCleanedUp = false;

    const checkAllReady = () => {
      if (isCleanedUp) return;
      if (videosReady.size === totalVideos) {
        setLoading(false);
      }
    };

    // Trigger aggressive pre-buffering for all videos
    const preBufferVideos = () => {
      if (isCleanedUp) return;
      
      videoRefsArray.current.forEach((video, index) => {
        if (video && !videosReady.has(index)) {
          // Set preload to auto
          video.preload = 'auto';
          
          // Check if already ready (readyState >= 2 means enough data for current position)
          if (video.readyState >= 2) {
            videosReady.add(index);
            checkAllReady();
          } else {
            // Listen for canplay (fires when enough data to start playing)
            // canplaythrough may never fire for WebM due to missing duration metadata
            const onCanPlay = () => {
              video.removeEventListener('canplay', onCanPlay);
              video.removeEventListener('loadeddata', onLoadedData);
              if (!videosReady.has(index)) {
                videosReady.add(index);
                checkAllReady();
              }
            };
            
            // Also listen for loadeddata as fallback (fires when first frame is available)
            const onLoadedData = () => {
              video.removeEventListener('loadeddata', onLoadedData);
              video.removeEventListener('canplay', onCanPlay);
              if (!videosReady.has(index)) {
                videosReady.add(index);
                checkAllReady();
              }
            };
            
            video.addEventListener('canplay', onCanPlay);
            video.addEventListener('loadeddata', onLoadedData);
            cleanupFns.push(() => {
              video.removeEventListener('canplay', onCanPlay);
              video.removeEventListener('loadeddata', onLoadedData);
            });
          }
          
          // Trigger load to start buffering immediately
          video.load();
          
          // For non-first videos, seek to beginning to trigger range request for start
          if (index > 0) {
            video.currentTime = 0;
          }
        }
      });
      
      // Check if all were already ready
      checkAllReady();
    };

    // Small delay to ensure video elements are mounted
    const timer = setTimeout(preBufferVideos, 100);
    
    // Timeout fallback: If videos don't load within 10 seconds, enable playback anyway
    const timeoutFallback = setTimeout(() => {
      if (!isCleanedUp && videosReady.size < totalVideos) {
        console.warn('[ProjectPlayer] Timeout waiting for videos, enabling playback anyway');
        setLoading(false);
      }
    }, 10000);

    return () => {
      isCleanedUp = true;
      clearTimeout(timer);
      clearTimeout(timeoutFallback);
      cleanupFns.forEach(fn => fn());
    };
  }, [visible, scenesWithVideos, useCompositor]);

  // Clean animation loop - single source of truth for playback (LEGACY MODE ONLY)
  // When using compositor mode, Remotion handles all playback internally
  useEffect(() => {
    if (isResettingRef.current || !visible || !isPlaying || loading || scenesWithVideos.length === 0 || useCompositor) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      lastFrameTimeRef.current = 0;
      return;
    }

    const animate = (timestamp: number) => {
      if (isResettingRef.current) return;
      
      if (lastFrameTimeRef.current === 0) {
        lastFrameTimeRef.current = timestamp;
      }

      const deltaSeconds = (timestamp - lastFrameTimeRef.current) / 1000;
      lastFrameTimeRef.current = timestamp;

      // Update timeline (single source of truth)
      const newTime = currentTimeRef.current + deltaSeconds;
      
      // When we reach the end of the compressed timeline, stop playback (don't reset)
      if (newTime >= totalDuration) {
        currentTimeRef.current = totalDuration;
        setCurrentTime(totalDuration);
        setIsPlaying(false);
        
        // Cancel animation frame
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        
        return;
      }
      
      currentTimeRef.current = newTime;
      
      // ===== SCENE DETECTION (Synchronous) =====
      let newState: PlaybackState = {
        sceneIndex: 0,
        isTransitioning: false,
        transitionType: 'cut',
        transitionProgress: 0,
      };
      
      // Find which scene/transition we're in
      for (let i = 0; i < scenesWithVideos.length; i++) {
        const scene = scenesWithVideos[i];
        const nextScene = scenesWithVideos[i + 1];
        const transition = scene.transitionOut || { type: 'cut', duration: 0 };
        const transitionDur = transition.type === 'cut' ? 0 : (transition.duration || 0);
        
        const sceneStart = scene.startTime;
        const sceneEnd = scene.endTime;
        const transitionStart = sceneEnd - transitionDur;
        
        // Before transition: normal playback
        if (newTime >= sceneStart && newTime < transitionStart) {
          newState = {
            sceneIndex: i,
            isTransitioning: false,
            transitionType: 'cut',
            transitionProgress: 0,
          };
          break;
        }
        
        // In transition: blend two scenes
        if (nextScene && newTime >= transitionStart && newTime < sceneEnd) {
          newState = {
            sceneIndex: i,
            isTransitioning: true,
            transitionType: transition.type,
            transitionProgress: (newTime - transitionStart) / transitionDur,
          };
          
          break;
        }
        
        // Past this scene: move to next
        if (newTime >= sceneEnd) {
          if (nextScene) {
            continue; // Check next scene
          } else {
            // Last scene - we're past its end but still playing
            newState = {
              sceneIndex: i,
              isTransitioning: false,
              transitionType: 'cut',
              transitionProgress: 0,
            };
            break; // Don't continue the loop
          }
        }
      }
      
      // Fallback: if no scene matched, stay on the last scene (don't default to 0)
      if (newState.sceneIndex === 0 && scenesWithVideos.length > 0) {
        const lastSceneIndex = scenesWithVideos.length - 1;
        const lastScene = scenesWithVideos[lastSceneIndex];
        
        // If we're past all scenes, show the last scene
        if (newTime >= lastScene.startTime) {
          newState = {
            sceneIndex: lastSceneIndex,
            isTransitioning: false,
            transitionType: 'cut',
            transitionProgress: 0,
          };
        }
      }
      
      // Update ref immediately (synchronous)
      playbackStateRef.current = newState;
      
      // ===== VIDEO SYNC (Direct manipulation) =====
      const currentScene = scenesWithVideos[newState.sceneIndex];
      const currentVideo = videoRefsArray.current[newState.sceneIndex];
      
      if (currentVideo && currentScene) {
        // Play current video
        if (currentVideo.paused) {
          currentVideo.play().catch(() => {});
        }
        
        // Sync video time to timeline
        const sceneLocalTime = newTime - currentScene.startTime;
        const targetTime = Math.max(0, Math.min(sceneLocalTime, currentScene.duration / 1000 - 0.01));
        const diff = Math.abs(targetTime - currentVideo.currentTime);
        
        if (diff > 0.15) {
          currentVideo.currentTime = targetTime;
        }
      }
      
      // During transition: play next video (synced to transition progress)
      if (newState.isTransitioning) {
        const nextScene = scenesWithVideos[newState.sceneIndex + 1];
        const nextVideo = videoRefsArray.current[newState.sceneIndex + 1];
        
        if (nextVideo && nextScene) {
          // Play the video if paused
          if (nextVideo.paused) {
            nextVideo.play().catch(() => {});
          }
          
          // Sync next video to transition progress
          // Transition is 50/50: next scene plays from 0 to (transitionDuration / 2)
          const transition = scenesWithVideos[newState.sceneIndex].transitionOut || { type: 'cut', duration: 0 };
          const transitionDur = transition.type === 'cut' ? 0 : (transition.duration || 0);
          const nextVideoTime = newState.transitionProgress * (transitionDur / 2);
          const diff = Math.abs(nextVideoTime - nextVideo.currentTime);
          
          // Sync if difference is significant
          if (diff > 0.1) {
            nextVideo.currentTime = nextVideoTime;
          }
        }
      }
      
      // ===== UI STATE UPDATE (Throttled to reduce re-renders) =====
      // CRITICAL: Update refs BEFORE calling setState to prevent infinite loops
      // The animation loop runs faster than React's render cycle, so we must
      // track the "intended" state in refs to avoid calling setState repeatedly
      
      if (newState.sceneIndex !== currentSceneIndexRef.current) {
        currentSceneIndexRef.current = newState.sceneIndex; // Update ref FIRST
        setCurrentSceneIndex(newState.sceneIndex);
      }
      
      if (newState.isTransitioning !== isTransitioningRef.current) {
        isTransitioningRef.current = newState.isTransitioning; // Update ref FIRST
        setIsTransitioning(newState.isTransitioning);
      }
      
      if (newState.isTransitioning) {
        setTransitionProgress(newState.transitionProgress);
      }
      
      // Throttle currentTime state updates to every 100ms (10 fps for UI) instead of every frame
      // This significantly reduces re-renders while keeping playback smooth
      const now = performance.now();
      if (now - lastTimeUpdateRef.current >= 100) {
        setCurrentTime(newTime);
        lastTimeUpdateRef.current = now;
      }

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [visible, isPlaying, loading, scenesWithVideos, totalDuration, performPlaybackReset, useCompositor]);

  // EDGE CASE HANDLER: Last scene's video 'ended' event (LEGACY MODE ONLY)
  // This catches the case where the last video ends naturally before reaching totalDuration
  // Use refs instead of state to avoid setup/teardown on every isPlaying change
  useEffect(() => {
    // Skip in compositor mode - Remotion handles video lifecycle
    if (!visible || scenesWithVideos.length === 0 || useCompositor) {
      return;
    }

    const lastSceneIndex = scenesWithVideos.length - 1;
    const lastVideo = videoRefsArray.current[lastSceneIndex];

    if (!lastVideo) {
      return;
    }

    const handleLastVideoEnded = () => {
      // Use ref to check if we're actually playing (avoids stale closure)
      if (isPlayingRef.current) {
        performPlaybackReset();
      }
    };

    lastVideo.addEventListener('ended', handleLastVideoEnded);

    return () => {
      lastVideo.removeEventListener('ended', handleLastVideoEnded);
    };
  }, [visible, scenesWithVideos.length, performPlaybackReset, useCompositor]);

  // Pause all videos when playback stops (LEGACY MODE ONLY)
  useEffect(() => {
    // Skip in compositor mode - Remotion handles video lifecycle
    if (useCompositor) {
      return;
    }
    if (!isPlaying) {
      videoRefsArray.current.forEach(video => {
        if (video && !video.paused) {
          video.pause();
        }
      });
    }
  }, [isPlaying, useCompositor]);

  // Stop and reset compositor when modal closes (COMPOSITOR MODE)
  useEffect(() => {
    if (!useCompositor) {
      return;
    }
    
    // When modal becomes invisible, stop and reset compositor
    if (!visible) {
      if (compositorRef.current) {
        compositorRef.current.pause();
        compositorRef.current.seekToSeconds(0);
      }
      // Reset state
      setIsPlaying(false);
      setCurrentTime(0);
      currentTimeRef.current = 0;
    }
  }, [visible, useCompositor]);

  // Calculate opacity/transform for each video during transitions
  const getVideoVisualState = (sceneIndex: number) => {
    // Not transitioning: only current scene is visible
    if (!isTransitioning) {
      return {
        opacity: sceneIndex === currentSceneIndex ? 1 : 0,
        transform: 'translateX(0)',
        zIndex: sceneIndex === currentSceneIndex ? 2 : 1,
      };
    }

    // During transition
    const progress = transitionProgress;
    const isCurrentScene = sceneIndex === currentSceneIndex;
    const isNextScene = sceneIndex === currentSceneIndex + 1;

    if (!isCurrentScene && !isNextScene) {
      // Not involved in transition
      return {
        opacity: 0,
        transform: 'translateX(0)',
        zIndex: 1,
      };
    }

    let opacity = 0;
    let transform = 'translateX(0)';
    let zIndex = isCurrentScene ? 2 : 3; // Next scene on top during transition

    // Use the transition type from playback state
    const transitionType = playbackStateRef.current.transitionType;

    switch (transitionType) {
      case 'fade':
        if (isCurrentScene) {
          opacity = progress < 0.5 ? 1 - (progress * 2) : 0;
        } else {
          opacity = progress >= 0.5 ? ((progress - 0.5) * 2) : 0;
        }
        break;
      case 'crossfade':
        opacity = isCurrentScene ? 1 - progress : progress;
        break;
      case 'slide':
        opacity = 1;
        transform = isCurrentScene 
          ? `translateX(-${progress * 100}%)` 
          : `translateX(${(1 - progress) * 100}%)`;
        break;
      default:
        opacity = isCurrentScene ? 1 - progress : progress;
    }

    return { opacity, transform, zIndex };
  };

  // Handle play/pause toggle
  const togglePlayPause = useCallback(() => {
    console.log('[ProjectPlayerModal] togglePlayPause called', {
      useCompositor,
      hasCompositorRef: !!compositorRef.current,
      compositorReady,
      isPlaying,
    });
    
    if (useCompositor) {
      if (!compositorRef.current) {
        console.warn('[ProjectPlayerModal] Compositor ref not ready');
        return;
      }
      
      const wasPlaying = compositorRef.current.isPlaying();
      console.log('[ProjectPlayerModal] Compositor wasPlaying:', wasPlaying);
      
      if (wasPlaying) {
        compositorRef.current.pause();
      } else {
        compositorRef.current.play();
      }
      setIsPlaying(!wasPlaying);
      return;
    }
    
    if (isResettingRef.current) {
      isResettingRef.current = false;
    }
    
    // Reset lastFrameTime when starting playback
    if (!isPlaying) {
      lastFrameTimeRef.current = 0;
    }
    
    setIsPlaying((prev) => !prev);
  }, [isPlaying, useCompositor, compositorReady]);

  // Skip to previous scene
  const skipToPrevious = useCallback(() => {
    if (currentSceneIndex > 0) {
      // Pause all videos to prevent audio overlap
      pauseAllVideosRef.current();
      
      const prevScene = scenesWithVideos[currentSceneIndex - 1];
      setCurrentSceneIndex(currentSceneIndex - 1);
      setCurrentTime(prevScene.startTime);
      currentTimeRef.current = prevScene.startTime;
      lastFrameTimeRef.current = 0;
    }
  }, [currentSceneIndex, scenesWithVideos]);

  // Skip to next scene
  const skipToNext = useCallback(() => {
    if (currentSceneIndex < scenesWithVideos.length - 1) {
      // Pause all videos to prevent audio overlap
      pauseAllVideosRef.current();
      
      const nextScene = scenesWithVideos[currentSceneIndex + 1];
      setCurrentSceneIndex(currentSceneIndex + 1);
      setCurrentTime(nextScene.startTime);
      currentTimeRef.current = nextScene.startTime;
      setIsTransitioning(false);
      lastFrameTimeRef.current = 0;
    }
  }, [currentSceneIndex, scenesWithVideos]);

  // Keyboard shortcuts (Enhanced)
  useEffect(() => {
    if (!visible) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      // Prevent if user is typing in an input
      if ((e.target as HTMLElement)?.tagName === 'INPUT' || (e.target as HTMLElement)?.tagName === 'TEXTAREA') {
        return;
      }
      
      // Disable playback shortcuts while loading
      if (loading) {
        return;
      }

      switch (e.key) {
        case ' ':
          e.preventDefault();
          togglePlayPause();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          // Seek backward 5 seconds
          {
            const newTime = Math.max(0, currentTime - 5);
            if (useCompositor && compositorRef.current) {
              compositorRef.current.seekToSeconds(newTime);
            } else {
              pauseAllVideosRef.current();
              currentTimeRef.current = newTime;
            }
            setCurrentTime(newTime);
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          // Seek forward 5 seconds
          {
            const newTime = Math.min(totalDuration, currentTime + 5);
            if (useCompositor && compositorRef.current) {
              compositorRef.current.seekToSeconds(newTime);
            } else {
              pauseAllVideosRef.current();
              currentTimeRef.current = newTime;
            }
            setCurrentTime(newTime);
          }
          break;
        case 'j':
          e.preventDefault();
          // Seek backward 10 seconds
          {
            const newTime = Math.max(0, currentTime - 10);
            if (useCompositor && compositorRef.current) {
              compositorRef.current.seekToSeconds(newTime);
            } else {
              pauseAllVideosRef.current();
              currentTimeRef.current = newTime;
            }
            setCurrentTime(newTime);
          }
          break;
        case 'l':
          e.preventDefault();
          // Seek forward 10 seconds
          {
            const newTime = Math.min(totalDuration, currentTime + 10);
            if (useCompositor && compositorRef.current) {
              compositorRef.current.seekToSeconds(newTime);
            } else {
              pauseAllVideosRef.current();
              currentTimeRef.current = newTime;
            }
            setCurrentTime(newTime);
          }
          break;
        case 'k':
          e.preventDefault();
          togglePlayPause();
          break;
        case ',':
          e.preventDefault();
          // Previous scene
          if (useCompositor && compositorRef.current) {
            // Calculate scene start times considering transitions
            const sceneStartTimes: number[] = [];
            let cumulativeTime = 0;
            for (let i = 0; i < compositorSceneInputs.length; i++) {
              sceneStartTimes.push(cumulativeTime);
              const input = compositorSceneInputs[i];
              const sceneDuration = (input.recordingData?.duration || input.scene.duration || 0) / 1000;
              const transitionDuration = ((input.scene as any).metadata as any)?.transitionOut?.duration || 0;
              cumulativeTime += sceneDuration;
              // Subtract transition overlap (except for last scene)
              if (i < compositorSceneInputs.length - 1) {
                cumulativeTime -= transitionDuration;
              }
            }
            // Find current scene index based on currentTime
            let currentIdx = 0;
            for (let i = sceneStartTimes.length - 1; i >= 0; i--) {
              if (currentTime >= sceneStartTimes[i]) {
                currentIdx = i;
                break;
              }
            }
            // Go to previous scene (or start of current if not at start)
            const targetIdx = currentTime > sceneStartTimes[currentIdx] + 0.5 ? currentIdx : Math.max(0, currentIdx - 1);
            const targetTime = sceneStartTimes[targetIdx];
            compositorRef.current.seekToSeconds(targetTime);
            setCurrentTime(targetTime);
          } else {
            skipToPrevious();
          }
          break;
        case '.':
          e.preventDefault();
          // Next scene
          if (useCompositor && compositorRef.current) {
            // Calculate scene start times considering transitions
            const sceneStartTimes: number[] = [];
            let cumulativeTime = 0;
            for (let i = 0; i < compositorSceneInputs.length; i++) {
              sceneStartTimes.push(cumulativeTime);
              const input = compositorSceneInputs[i];
              const sceneDuration = (input.recordingData?.duration || input.scene.duration || 0) / 1000;
              const transitionDuration = ((input.scene as any).metadata as any)?.transitionOut?.duration || 0;
              cumulativeTime += sceneDuration;
              // Subtract transition overlap (except for last scene)
              if (i < compositorSceneInputs.length - 1) {
                cumulativeTime -= transitionDuration;
              }
            }
            // Find current scene index based on currentTime
            let currentIdx = 0;
            for (let i = sceneStartTimes.length - 1; i >= 0; i--) {
              if (currentTime >= sceneStartTimes[i]) {
                currentIdx = i;
                break;
              }
            }
            // Go to next scene
            const targetIdx = Math.min(compositorSceneInputs.length - 1, currentIdx + 1);
            const targetTime = sceneStartTimes[targetIdx];
            compositorRef.current.seekToSeconds(targetTime);
            setCurrentTime(targetTime);
          } else {
            skipToNext();
          }
          break;
        case 'Escape':
          onClose();
          break;
        case '0':
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
        case '6':
        case '7':
        case '8':
        case '9':
          e.preventDefault();
          // Seek to percentage (0 = 0%, 1 = 10%, ..., 9 = 90%)
          {
            const percentage = parseInt(e.key) / 10;
            const newTime = totalDuration * percentage;
            if (useCompositor && compositorRef.current) {
              compositorRef.current.seekToSeconds(newTime);
            } else {
              pauseAllVideosRef.current();
              currentTimeRef.current = newTime;
            }
            setCurrentTime(newTime);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [visible, loading, currentTime, totalDuration, togglePlayPause, skipToPrevious, skipToNext, onClose, useCompositor, compositorSceneInputs]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const currentScene = scenesWithVideos[currentSceneIndex];

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1000}
      centered
      className="project-player-modal"
      closeIcon={<X size={20} />}
    >
      <div ref={containerRef} className="project-player-container">
        {loading ? (
          <div className="loading-container">
            <Spin size="large" />
            <p>Loading project videos...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p>{error}</p>
          </div>
        ) : (useCompositor ? compositorSceneInputs.length === 0 : scenesWithVideos.length === 0) ? (
          <div className="empty-container">
            <p>No recorded scenes found</p>
          </div>
        ) : (
          <>
            {/* Video Container */}
            <div className="video-container">
              {useCompositor ? (
                <MultiSceneCompositorPlayer
                  ref={compositorRef}
                  sceneInputs={compositorSceneInputs}
                  autoPlay={false}
                  loop={false}
                  showControls={true}
                  onTimeUpdate={handleCompositorTimeUpdate}
                  onPlay={handleCompositorPlay}
                  onPause={handleCompositorPause}
                  onEnded={handleCompositorEnded}
                  onReady={handleCompositorReady}
                  style={{ width: '100%', height: '100%' }}
                />
              ) : (
                scenesWithVideos.map((scene, index) => {
                  const visualState = getVideoVisualState(index);
                  return (
                    <div
                      key={scene.id}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        opacity: visualState.opacity,
                        transform: visualState.transform,
                        zIndex: visualState.zIndex,
                        pointerEvents: index === currentSceneIndex ? 'auto' : 'none',
                      }}
                    >
                      <video
                        ref={(el) => {
                          videoRefsArray.current[index] = el;
                        }}
                        src={scene.videoUrl}
                        muted={false}
                        playsInline
                        preload="auto"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    </div>
                  );
                })
              )}

              {/* Scene Title Overlay - only for legacy mode */}
              {!useCompositor && (
                <div className="scene-title-overlay">
                  {currentScene?.title || `Scene ${currentSceneIndex + 1}`}
                </div>
              )}

            </div>

            {/* Controls - Only show custom controls for legacy (non-compositor) mode */}
            {!useCompositor && (
              <div className="controls-container">
                <div className="playback-controls">
                  <Tooltip title="Previous Scene (←)">
                    <Button
                      type="text"
                      icon={<SkipBack size={20} />}
                      onClick={skipToPrevious}
                      disabled={loading || currentSceneIndex === 0}
                    />
                  </Tooltip>

                  <Tooltip title={loading ? 'Loading...' : (isPlaying ? 'Pause (Space)' : 'Play (Space)')}>
                    <Button
                      type="text"
                      icon={isPlaying ? <Pause size={24} fill="currentColor" /> : <Play size={24} fill="currentColor" />}
                      onClick={togglePlayPause}
                      disabled={loading}
                    />
                  </Tooltip>

                  <Tooltip title="Next Scene (→)">
                    <Button
                      type="text"
                      icon={<SkipForward size={20} />}
                      onClick={skipToNext}
                      disabled={loading || currentSceneIndex === scenesWithVideos.length - 1}
                    />
                  </Tooltip>
                </div>

                <div className="timeline-container">
                  <span className="time-label">{formatTime(currentTime)}</span>
                  
                  {/* Single Linear Progress Bar with Scene Markers */}
                  <div className="linear-progress-wrapper">
                    <div 
                      className="linear-progress-bar"
                      onMouseDown={(e) => {
                        // Linear seek: click anywhere on the bar to seek to that time
                        const progressBarElement = e.currentTarget;
                        const rect = progressBarElement.getBoundingClientRect();
                        const clickX = e.clientX - rect.left;
                        const clickPercentage = Math.max(0, Math.min(1, clickX / rect.width));
                        
                        // Calculate absolute time from click position
                        const absoluteTime = clickPercentage * totalDuration;
                        
                        // Pause all videos to prevent audio overlap during seek
                        pauseAllVideosRef.current();
                        
                        // Find which scene this time belongs to
                        let targetSceneIndex = 0;
                        let sceneLocalTime = 0;
                        
                        for (let i = 0; i < scenesWithVideos.length; i++) {
                          const scene = scenesWithVideos[i];
                          if (absoluteTime >= scene.startTime && absoluteTime < scene.endTime) {
                            targetSceneIndex = i;
                            sceneLocalTime = absoluteTime - scene.startTime;
                            break;
                          } else if (i === scenesWithVideos.length - 1 && absoluteTime >= scene.startTime) {
                            // Last scene edge case
                            targetSceneIndex = i;
                            sceneLocalTime = Math.min(absoluteTime - scene.startTime, (scene.duration / 1000) - 0.01);
                            break;
                          }
                        }
                        
                        // Update timeline ref
                        currentTimeRef.current = absoluteTime;
                        lastFrameTimeRef.current = 0;
                        
                        // Seek the target video
                        const targetVideo = videoRefsArray.current[targetSceneIndex];
                        if (targetVideo) {
                          targetVideo.currentTime = sceneLocalTime;
                        }
                        
                        // Update state
                        setCurrentTime(absoluteTime);
                        setCurrentSceneIndex(targetSceneIndex);
                      }}
                    >
                      {/* Background rail */}
                      <div className="progress-rail" />
                      
                      {/* Fill track - updates instantly as currentTime changes */}
                      <div 
                        className="progress-track" 
                        style={{ width: `${(currentTime / totalDuration) * 100}%` }}
                      />
                      
                      {/* Scene markers */}
                      {scenesWithVideos.map((scene, index) => {
                        if (index === 0) return null;
                        
                        const markerPosition = (scene.startTime / totalDuration) * 100;
                        
                        return (
                          <Tooltip
                            key={`marker-${scene.id}`}
                            title={
                              <div>
                                <div style={{ fontWeight: 600 }}>{scene.title || `Scene ${index + 1}`}</div>
                                <div style={{ fontSize: '12px', opacity: 0.8 }}>{formatTime(scene.startTime)}</div>
                              </div>
                            }
                            placement="top"
                          >
                            <div 
                              className="scene-marker"
                              style={{ left: `${markerPosition}%` }}
                            />
                          </Tooltip>
                        );
                      })}
                    </div>
                  </div>
                  
                  <span className="time-label">{formatTime(totalDuration)}</span>
                </div>
              </div>
            )}

            {/* Scene Progress */}
            <div className="scene-progress">
              {useCompositor 
                ? `${compositorSceneInputs.length} scene${compositorSceneInputs.length !== 1 ? 's' : ''}`
                : `Scene ${currentSceneIndex + 1} of ${scenesWithVideos.length}`
              }
            </div>

            {/* Keyboard Shortcuts Instructions */}
            <div className="keyboard-instructions">
              <div className="instruction-group">
                <kbd>Space</kbd> or <kbd>K</kbd> Play/Pause
              </div>
              <div className="instruction-group">
                <kbd>←</kbd> <kbd>→</kbd> ±5s
              </div>
              <div className="instruction-group">
                <kbd>J</kbd> <kbd>L</kbd> ±10s
              </div>
              <div className="instruction-group">
                <kbd>,</kbd> <kbd>.</kbd> Prev/Next Scene
              </div>
              <div className="instruction-group">
                <kbd>0</kbd>-<kbd>9</kbd> Seek to %
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ProjectPlayerModal;
